import React, { useState } from "react";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import { CommandList } from "cmdk";
import Button from "~/components/parts/Button";
import {
  Command,
  CommandGroup,
  CommandItem,
  CommandEmpty,
} from "~/components/parts/Command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "~/components/parts/Popover";
import { cn } from "~/utils";

export const courseLozengeColours = {
  ALL_SKILLS: "#ef4444", // bg-red-500
  CURRICULUM: "#7e22ce", // bg-purple-700
  SPECIFICATION: "#eab308", // bg-yellow-500
  SCHEME_OF_WORK: "#16a34a", // bg-green-600
};

export const courseLozengeLabels = {
  ALL_SKILLS: "All",
  CURRICULUM: "NC",
  SPECIFICATION: "Spec",
  SCHEME_OF_WORK: "SoL",
};

export const courseLozengeFullLabels = {
  ALL_SKILLS: "All Skills",
  CURRICULUM: "Curriculum",
  SPECIFICATION: "Specification",
  SCHEME_OF_WORK: "Scheme of Learning",
};

export const CourseTypeLonzenge = ({ className, fullLabel = false, type }) => (
  <div
    className={cn(
      "w-10 flex-none rounded px-0.5 py-1 text-center text-2xs text-white",
      className,
    )}
    style={{ backgroundColor: courseLozengeColours[type] }}
  >
    {fullLabel ? courseLozengeFullLabels[type] : courseLozengeLabels[type]}
  </div>
);

function CourseTile({ className, course }) {
  const { authorName, name, type } = course;

  return (
    <div className={cn("flex items-center gap-3", className)}>
      <CourseTypeLonzenge type={type} />
      <div className="flex flex-col gap-0.5 text-left">
        {authorName && (
          <span className="flex items-center gap-1 text-2xs text-gray-500">
            <span>{authorName}</span>
            <ArrowForwardIcon fontSize="inherit" />
          </span>
        )}
        <span className="text-lg font-semibold leading-none lg:text-base lg:leading-none">
          {name}
        </span>
      </div>
    </div>
  );
}

export default function CoursesDropdown({
  courseGroups,
  disabled = false,
  onChange: controlledOnChange = null,
  startOpen = false,
  value: controlledValue = null,
}) {
  const [open, setOpen] = useState(startOpen);

  /**
   * If the component is passed an onChange prop, the component is
   * controlled. Set the handler and value accordingly.
   */
  const [uncontrolledValue, setUncontrolledValue] = useState(null);
  const uncontrolledOnChange = (value) =>
    setUncontrolledValue((state) => (state === value ? null : value));
  const onChange = controlledOnChange
    ? controlledOnChange
    : uncontrolledOnChange;
  const value = controlledOnChange ? controlledValue : uncontrolledValue;

  // Provide a warning when component is passed a value without a handler.
  if (controlledValue && !controlledOnChange) {
    console.error(
      "Warning: You provided a `value` prop to a CoursesDropdown component without an `onChange` handler. Please provide an `onChange` handler to use as a controlled component.",
    );
  }

  // Find the course associated with the current component value.
  const allCourses = courseGroups?.length
    ? courseGroups.map((group) => group.courses).reduce((a, b) => [...a, ...b])
    : [];
  const selectedCourse = value
    ? allCourses.find((c) => c.coid === value)
    : null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          disabled={disabled}
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            /**
             * First line of class names comes from the shadcn button component.
             * https://ui.shadcn.com/docs/components/button
             **/
            "inline-flex items-center justify-center rounded-md text-sm font-medium outline-none ring-gray-400 ring-offset-2 transition-colors focus-visible:ring-2 disabled:pointer-events-none",
            "h-auto min-h-14 w-full justify-between border border-gray-200 bg-white px-3 text-gray-700 hover:bg-gray-50",
            value ? "" : "text-gray-500 hover:text-gray-500",
            "select-none disabled:bg-inherit disabled:text-gray-700 disabled:opacity-100",
          )}
        >
          {selectedCourse ? (
            <CourseTile course={selectedCourse} />
          ) : (
            "Select course..."
          )}
          {!disabled && <UnfoldMoreIcon className="flex-none" fontSize="small" />}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        /**
         * asChild prop needed for command list keyboard navigation.
         * https://github.com/pacocoursey/cmdk/issues/220#issuecomment-1975371524
         */
        asChild
        /**
         * Set the Popover content width to the same width as the trigger.
         * https://www.radix-ui.com/primitives/docs/components/popover#examples
         */
        className="max-h-[400px] w-[var(--radix-popover-trigger-width)] overflow-y-auto p-0"
      >
        <Command>
          <CommandList>
            <CommandEmpty>No courses found</CommandEmpty>
            {courseGroups.map(
              ({ heading, courses, link }, i) =>
                (courses.length > 0 || link) && (
                  <CommandGroup heading={heading} key={`course-group-${i + 1}`}>
                    {courses.map((course) => (
                      <CommandItem
                        key={course.coid}
                        className="min-h-12 py-2"
                        onSelect={() => {
                          onChange(course.coid);
                          setOpen(false);
                        }}
                        value={course.coid.toString()}
                      >
                        <CourseTile course={course} className="pr-[15px]" />
                      </CommandItem>
                    ))}
                    {link && (
                      <CommandItem
                        className="my-1.5 cursor-pointer select-none justify-center bg-blue-50 bg-opacity-40 p-1.5 text-xs text-blue-500 data-[selected=true]:bg-blue-50 data-[selected=true]:bg-opacity-90 data-[selected=true]:text-blue-600"
                        onSelect={() => (window.location = link.href)}
                      >
                        {link.label}
                      </CommandItem>
                    )}
                  </CommandGroup>
                ),
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
